"use client"

import * as React from "react"
import {
  DayPicker,
  getDefaultClassNames,
  type DayButton,
  type Locale,
} from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from "lucide-react"

// Year-dropdown navigable span. Matches the Flutter app, whose date pickers
// use Syncfusion SfDateRangePicker without an explicit minDate/maxDate
// (scrips_core/lib/utils/date_time_utils.dart) — so they fall back to
// Syncfusion's default 1900–2100 range. We mirror that span here. Per-field
// limits (e.g. "DOB can't be in the future") are still applied by individual
// consumers via the `disabled` prop, exactly like Flutter's
// selectableDayPredicate.
const PICKER_MIN_YEAR = 1900
const PICKER_MAX_YEAR = 2100

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  // Year navigation (approved 2026-06-23): default to the month + year
  // dropdown caption so users can jump straight to any year. The old "label"
  // caption only offered prev/next month arrows — reaching a 1990 DOB from
  // today meant ~430 clicks. Consumers can still pass captionLayout="label"
  // to opt out.
  captionLayout = "dropdown",
  buttonVariant = "ghost",
  locale,
  formatters,
  components,
  // Year-dropdown span defaults to 1900–2100 (Flutter parity, see constants
  // above). A consumer that passes startMonth/endMonth (or a disabled range)
  // overrides this.
  startMonth,
  endMonth,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  const defaultClassNames = getDefaultClassNames()
  const resolvedStartMonth = startMonth ?? new Date(PICKER_MIN_YEAR, 0)
  const resolvedEndMonth = endMonth ?? new Date(PICKER_MAX_YEAR, 11)

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      startMonth={resolvedStartMonth}
      endMonth={resolvedEndMonth}
      className={cn(
        "group/calendar bg-background p-2 [--cell-radius:var(--radius-md)] [--cell-size:--spacing(7)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        // Full month name ("June", not "Jun") to match the Signal-DS DatePicker
        // caption (calendar-internals uses react-day-picker's default long-name
        // dropdown). Keeping these in lockstep is the whole point of the restyle.
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: "long" }),
        ...formatters,
      }}
      classNames={{
        // Styled to match the Signal-DS DatePicker calendar (Claude Design):
        // bordered month/year dropdown pills, Blue/500 selected, today =
        // blue number + square ring, Blue/100 in-range band, 36px cells.
        root: cn("w-fit", defaultClassNames.root),
        months: cn("relative flex flex-col", defaultClassNames.months),
        month: cn("flex flex-col gap-3", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex items-center justify-between h-10",
          defaultClassNames.nav
        ),
        button_previous: cn(
          "inline-flex items-center justify-center h-8 w-8 rounded-[var(--radius-md)] text-[var(--color-text-muted)] hover:bg-[var(--color-grey-100)] disabled:opacity-40 disabled:pointer-events-none",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          "inline-flex items-center justify-center h-8 w-8 rounded-[var(--radius-md)] text-[var(--color-text-muted)] hover:bg-[var(--color-grey-100)] disabled:opacity-40 disabled:pointer-events-none",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-10 items-center justify-center",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex items-center justify-center gap-2",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "relative inline-flex items-center h-8 rounded-[8px] border border-[var(--color-border-strong)] bg-white",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "absolute inset-0 w-full h-full opacity-0 cursor-pointer",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none",
          captionLayout === "label"
            ? "text-size-sm font-medium text-[var(--color-text-primary)]"
            : "pointer-events-none flex items-center gap-1 px-3 text-size-sm font-medium text-[var(--color-text-primary)] [&>svg]:size-4 [&>svg]:text-[var(--color-text-muted)]",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "h-8 w-10 flex items-center justify-center text-size-xs font-normal text-[var(--color-text-muted)] select-none",
          defaultClassNames.weekday
        ),
        week: cn("flex mt-1", defaultClassNames.week),
        week_number_header: cn(
          "w-10 select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-size-xs text-[var(--color-text-muted)] select-none",
          defaultClassNames.week_number
        ),
        day: cn("p-0 text-center", defaultClassNames.day),
        // In-range band — Blue/100 tint on the cell; endpoints rounded outward.
        range_start: cn(
          "bg-[var(--color-blue-100)] rounded-l-[8px]",
          defaultClassNames.range_start
        ),
        range_middle: cn("bg-[var(--color-blue-100)]", defaultClassNames.range_middle),
        range_end: cn(
          "bg-[var(--color-blue-100)] rounded-r-[8px]",
          defaultClassNames.range_end
        ),
        outside: cn("text-[var(--color-text-hint)]", defaultClassNames.outside),
        disabled: cn(
          "text-[var(--color-text-hint)] opacity-40",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            )
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon className={cn("size-4", className)} {...props} />
            )
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          )
        },
        DayButton: ({ ...props }) => (
          <CalendarDayButton locale={locale} {...props} />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  // Endpoint = single-selected day, or a range start/end. Matches the Signal-DS
  // calendar: endpoints render Blue/500 fill; the in-range band is Blue/100
  // (tinted on the cell via `range_*` classNames above).
  const isEndpoint =
    modifiers.range_start ||
    modifiers.range_end ||
    (modifiers.selected && !modifiers.range_middle && !modifiers.outside)

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString(locale?.code)}
      className={cn(
        "flex items-center justify-center h-10 w-10 rounded-[8px] text-size-sm font-normal",
        "font-['Inter',system-ui,sans-serif] text-[var(--color-text-primary)]",
        "transition-colors duration-100 outline-none",
        "hover:bg-[var(--color-grey-100)]",
        "focus-visible:ring-2 focus-visible:ring-[var(--color-blue-400)]",
        modifiers.today &&
          !isEndpoint &&
          !modifiers.range_middle &&
          "font-semibold text-[var(--color-brand-primary)] ring-1 ring-inset ring-[var(--color-border-strong)]",
        modifiers.range_middle &&
          "rounded-none bg-transparent text-[var(--color-text-primary)] hover:bg-[var(--color-blue-100)]",
        isEndpoint &&
          "bg-[var(--color-brand-primary)] text-white hover:bg-[var(--color-brand-primary)] ring-0",
        modifiers.outside && "text-[var(--color-text-hint)]",
        modifiers.disabled &&
          "text-[var(--color-text-hint)] opacity-40 pointer-events-none hover:bg-transparent",
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
